import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress,
  Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, Stack, Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { supabase } from '../supabaseClient.ts';

type Reservation = {
  id: string;
  booking_type: string;
  start_date: string;
  end_date: string;
  status: string;
  option_expires_at: string | null;
  total_amount: number | null;
  cancellation_fee: number | null;
  cancellation_amount_paid: number | null;
  reservation_dogs: {
    dogs: { name: string } | null;
  }[];
};

type Preview = {
  cancellation_rule: string;
  cancellation_fee: number;
  amount_paid: number;
  amount_still_to_pay: number;
  refund_amount: number;
  action_required: string;
};

export const MyReservationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    loadReservations();
  }, [user]);

  async function loadReservations() {
    if (!supabase || !user) {
      setLoading(false);
      return;
    }

    const { data: family } = await supabase
      .from('families')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!family) {
      setError('Je klantprofiel kon niet worden geladen.');
      setLoading(false);
      return;
    }

    const { data, error: loadError } = await supabase
      .from('reservations')
      .select(`
        id, booking_type, start_date, end_date, status,
        option_expires_at, total_amount,
        cancellation_fee, cancellation_amount_paid,
        reservation_dogs ( dogs ( name ) )
      `)
      .eq('family_id', family.id)
      .order('start_date', { ascending: false });

    if (loadError) {
      setError('Je reserveringen konden niet worden geladen.');
    } else {
      setReservations((data ?? []) as Reservation[]);
    }

    setLoading(false);
  }

  const today = new Date().toISOString().slice(0, 10);

  const upcoming = reservations
    .filter(
      r =>
        r.end_date >= today &&
        !['cancelled', 'expired'].includes(r.status)
    )
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const history = reservations
    .filter(
      r =>
        r.end_date < today &&
        !['cancelled', 'expired'].includes(r.status)
    )
    .sort((a, b) => b.start_date.localeCompare(a.start_date));

  const cancelledOrExpired = reservations
    .filter(r => ['cancelled', 'expired'].includes(r.status))
    .sort((a, b) => b.start_date.localeCompare(a.start_date));

  const date = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(`${value}T12:00:00`));

  const expiry = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));

  const money = (value: number) =>
    new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(Number(value));

  const dogs = (r: Reservation) =>
    r.reservation_dogs
      .map(x => x.dogs?.name)
      .filter(Boolean)
      .join(', ');

  const activeOption = (r: Reservation) =>
    r.status === 'option' &&
    !!r.option_expires_at &&
    new Date(r.option_expires_at).getTime() > Date.now();

  const statusLabel = (s: string) => ({
    confirmed: 'Bevestigd',
    option: 'Optie',
    cancel_pending_payment: 'Wacht op betaling',
    cancelled: 'Geannuleerd',
    completed: 'Afgerond',
    expired: 'Verlopen'
  }[s] ?? s);

  async function confirmOption() {
    if (!supabase || !confirmId) return;

    setBusy(true);
    const { error: e } = await supabase.rpc(
      'confirm_customer_option',
      { p_reservation_id: confirmId }
    );

    if (e) {
      setError(e.message);
    } else {
      setReservations(list =>
        list.map(r =>
          r.id === confirmId
            ? { ...r, status: 'confirmed', option_expires_at: null }
            : r
        )
      );
      setSuccess('Je optie is definitief bevestigd.');

      // Zodra een klant een optie definitief maakt, start de offerte direct.
      // We wachten de aanroep even af zodat hij zeker naar Supabase vertrekt.
      // Een Moneybird-storing blokkeert de bevestigde reservering niet:
      // de dagelijkse Moneybird-run blijft het vangnet.
      try {
        const { data: quoteData, error: quoteError } =
          await supabase.functions.invoke(
            'moneybird-quote-start-v1',
            {
              body: {
                action: 'start',
                reservation_id: confirmId
              }
            }
          );

        if (quoteError) {
          console.error(
            'De Moneybird-offerte kon niet direct worden gestart:',
            quoteError
          );
        } else {
          console.log(
            'Moneybird-offerte direct gestart:',
            quoteData
          );
        }
      } catch (quoteError) {
        console.error(
          'De Moneybird-offerte kon niet direct worden gestart:',
          quoteError
        );
      }
    }

    setBusy(false);
    setConfirmId(null);
  }

  async function openCancellation(id: string) {
    if (!supabase) return;

    setBusy(true);
    setError('');
    setSuccess('');
    setPreview(null);

    const { data, error: e } = await supabase.rpc(
      'preview_customer_cancellation',
      { p_reservation_id: id }
    );

    if (e) {
      setError(e.message);
    } else if (data?.[0]) {
      setCancelId(id);
      setPreview(data[0] as Preview);
    } else {
      setError('De annuleringsberekening kon niet worden gemaakt.');
    }

    setBusy(false);
  }

  async function requestCancellation() {
    if (!supabase || !cancelId) return;

    setBusy(true);

    const { data, error: e } = await supabase.rpc(
      'request_customer_cancellation',
      { p_reservation_id: cancelId }
    );

    if (e) {
      setError(e.message);
      setBusy(false);
      return;
    }

    const result = data?.[0];

    if (result) {
      setReservations(list =>
        list.map(r =>
          r.id === cancelId
            ? {
                ...r,
                status: result.reservation_status,
                cancellation_fee: Number(result.cancellation_fee),
                cancellation_amount_paid: 0
              }
            : r
        )
      );
      setSuccess(result.message);
    }

    setBusy(false);
    setCancelId(null);
    setPreview(null);
  }

  const ReservationCard = ({ r }: { r: Reservation }) => {
    const fee = Number(r.cancellation_fee ?? 0);
    const paid = Number(r.cancellation_amount_paid ?? 0);
    const stillToPay = Math.max(0, fee - paid);

    return (
      <Paper variant="outlined" sx={{ p: 2, boxShadow: 'none' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography fontWeight={800}>
              {date(r.start_date)}
              {r.booking_type === 'logeren' &&
                r.end_date !== r.start_date &&
                ` t/m ${date(r.end_date)}`}
            </Typography>

            <Typography color="text.secondary">
              {r.booking_type === 'dagopvang' ? 'Dagopvang' : 'Logeren'}
              {' · '}
              {dogs(r)}
            </Typography>

            {r.status === 'option' && r.option_expires_at && (
              <Typography variant="body2" color="warning.main">
                Optie geldig tot {expiry(r.option_expires_at)}
              </Typography>
            )}

            {r.total_amount !== null && (
              <Typography fontWeight={800} sx={{ mt: 1 }}>
                {money(r.total_amount)}
              </Typography>
            )}

            {r.status === 'cancel_pending_payment' && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                <Typography fontWeight={800}>
                  Annulering aangevraagd
                </Typography>
                <Typography variant="body2">
                  Annuleringskosten: {money(fee)}
                </Typography>
                <Typography variant="body2">
                  Betaald: {money(paid)}
                </Typography>
                <Typography variant="body2" fontWeight={800}>
                  Nog te betalen: {money(stillToPay)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  De gereserveerde plek blijft geblokkeerd totdat
                  de betaling is verwerkt.
                </Typography>
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              {activeOption(r) && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={() => setConfirmId(r.id)}
                >
                  Optie definitief maken
                </Button>
              )}

              {(r.status === 'confirmed' || activeOption(r)) && (
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => openCancellation(r.id)}
                >
                  Annuleren
                </Button>
              )}

              {r.end_date < today &&
                !['cancelled', 'expired'].includes(r.status) && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RateReviewRoundedIcon />}
                    onClick={() =>
                      navigate(`/mijn-pippaya/beoordeling?reservation=${r.id}`)
                    }
                  >
                    Beoordeling achterlaten
                  </Button>
                )}
            </Stack>
          </Box>

          <Chip
            size="small"
            label={statusLabel(r.status)}
            color={
              r.status === 'confirmed'
                ? 'success'
                : r.status === 'option'
                  ? 'warning'
                  : 'default'
            }
          />
        </Stack>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: 4 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/mijn-pippaya')}
        >
          Terug naar Mijn Pippaya
        </Button>

        <Typography variant="overline" color="text.secondary">
          Mijn Pippaya
        </Typography>
        <Typography variant="h3">Mijn reserveringen</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Bekijk je komende boekingen, opties en eerdere opvang.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}

        <Card sx={{ p: 2.5, mt: 3 }}>
          <Typography variant="h5">Komende reserveringen</Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {upcoming.length
              ? upcoming.map(r => <ReservationCard key={r.id} r={r} />)
              : <Alert severity="info">Je hebt geen komende reserveringen.</Alert>}
          </Stack>
        </Card>

        <Card sx={{ p: 2.5, mt: 2.5 }}>
          <Typography variant="h5">Eerdere reserveringen</Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {history.length
              ? history.map(r => <ReservationCard key={r.id} r={r} />)
              : <Typography color="text.secondary">
                  Er zijn nog geen eerdere reserveringen.
                </Typography>}
          </Stack>
        </Card>

        <Card sx={{ p: 2.5, mt: 2.5 }}>
          <Typography variant="h5">
            Geannuleerde / verlopen reserveringen
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {cancelledOrExpired.length
              ? cancelledOrExpired.map(r => (
                  <ReservationCard key={r.id} r={r} />
                ))
              : <Typography color="text.secondary">
                  Er zijn geen geannuleerde of verlopen reserveringen.
                </Typography>}
          </Stack>
        </Card>
      </Container>

      <Dialog open={!!confirmId} onClose={() => !busy && setConfirmId(null)}>
        <DialogTitle>Optie definitief maken?</DialogTitle>
        <DialogContent>
          Wil je deze 3-dagenoptie nu definitief reserveren?
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmId(null)}>
            Annuleren
          </Button>
          <Button variant="contained" disabled={busy} onClick={confirmOption}>
            Ja, definitief reserveren
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!cancelId && !!preview}
        onClose={() => {
          if (!busy) {
            setCancelId(null);
            setPreview(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reservering annuleren?</DialogTitle>

        {preview && (
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {preview.cancellation_rule}
            </Alert>

            <Stack spacing={1}>
              <Typography>
                Annuleringskosten: <strong>{money(preview.cancellation_fee)}</strong>
              </Typography>
              <Typography>
                Reeds betaald: <strong>{money(preview.amount_paid)}</strong>
              </Typography>
              <Typography>
                Nog te betalen: <strong>{money(preview.amount_still_to_pay)}</strong>
              </Typography>

              {preview.refund_amount > 0 && (
                <Typography>
                  Terug te betalen: <strong>{money(preview.refund_amount)}</strong>
                </Typography>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>{preview.action_required}</Typography>
          </DialogContent>
        )}

        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setCancelId(null);
              setPreview(null);
            }}
          >
            Terug
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={requestCancellation}
          >
            {busy ? 'Bezig...' : 'Annulering aanvragen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};